import {
    CANCELLED,
    CREATED,
    ORDERING,
    POST_PROCESSING,
    PRE_PROCESSING,
    PROCESSED,
    PROCESSING,
    REFUNDED,
    VOIDED,
} from '@/src/modules/OrderStatus/strings'
import {
    IOrderStatusLineItem,
    TBoxOrderStatusItem,
    TKycApplicationStatuses,
} from '@/src/modules/OrderStatus/types'

export function getLineItemsForOrderStatus(lineItems: IOrderStatusLineItem[]) {
    if (!lineItems) {
        return {
            lineItemsInBoxes: [],
            lineItemsNotInBox: [],
        }
    }

    const lineItemsNotInBox: IOrderStatusLineItem[] = []
    const lineItemsInBoxes: TBoxOrderStatusItem[] = []

    lineItems.forEach((item) => {
        const boxUuid: string | undefined = item?.customization?.box_uuid

        if (boxUuid) {
            // see if its box already exists. if it does, put the item in that box. if not, add a new box
            const existingBox: TBoxOrderStatusItem | undefined =
                lineItemsInBoxes?.find((box) => box.box_uuid === boxUuid)

            // if the box exists, add this item's total price and the item to the box
            if (existingBox) {
                existingBox.box_total_load_value += Number(item.denomination)
                existingBox.items.push(item)
                return
            }

            lineItemsInBoxes.push({
                box_uuid: boxUuid,
                box_total_load_value: Number(item.denomination),
                items: [item],
            })
            return
        }
        // if an items isn't in a box, add it to this array
        lineItemsNotInBox.push(item)
    })

    return {
        lineItemsInBoxes: lineItemsInBoxes,
        lineItemsNotInBox: lineItemsNotInBox,
    }
}

export function determineReviewStatus(
    pending_email_verification: boolean,
    pending_fraud_review: boolean,
    pending_artwork_review: boolean,
    kyc_account_review_status: TKycApplicationStatuses
) {
    if (pending_fraud_review) {
        return 'Pending Fraud Review'
    }
    if (pending_artwork_review) {
        return 'Pending Artwork Review'
    }
    if (pending_email_verification) {
        return 'Pending Email Verification'
    }

    if (kyc_account_review_status) {
        return kyc_account_review_status === 'rejected'
            ? 'Account Review Rejected'
            : 'Pending Account Review'
    }

    return 'Pending'
}

export function getCustomerFacingOrderStatus(
    order_status: string,
    pending_email_verification: boolean,
    pending_fraud_review: boolean,
    pending_artwork_review: boolean,
    kyc_account_review_status: TKycApplicationStatuses,
    isComplete: boolean,
    isFullyRefunded: boolean
) {
    if (isFullyRefunded) {
        return 'Refunded'
    }

    if (isComplete) {
        return 'Completed'
    }

    const reviewStatus: string = determineReviewStatus(
        pending_email_verification,
        pending_fraud_review,
        pending_artwork_review,
        kyc_account_review_status
    )

    switch (order_status) {
        case CANCELLED:
            return 'Cancelled'
        case ORDERING:
            return reviewStatus
        case PRE_PROCESSING:
            return 'Pending'
        case PROCESSING:
            return 'Processing'
        case POST_PROCESSING:
            return 'Processing'
        case PROCESSED:
            return 'Processed'
        default:
            return order_status
    }
}

export function getCustomerFacingItemStatus(
    redemptionEmailsSent: boolean,
    item: string,
    orderStatus: string,
    isPlastic: boolean,
    isVoided: boolean,
    isUSPS: boolean,
    lineItem: IOrderStatusLineItem,
    eGiftOpened: boolean,
    fullyRefunded: boolean
) {
    if (orderStatus === CANCELLED) {
        return 'Cancelled'
    } else if (
        lineItem.design?.requiresApproval &&
        lineItem.design?.approvalDecision !== 'approved'
    ) {
        if (!lineItem.design?.approvalDecision) {
            return 'Pending Artwork Review'
        } else if (lineItem.design?.approvalDecision === 'rejected') {
            return (
                <span>
                    Voided due to{' '}
                    <a href={'/image-guidelines'}>Artwork Review</a>
                </span>
            )
        }
    } else if (fullyRefunded) {
        return REFUNDED
    } else if (isVoided) {
        return VOIDED
    } else if (eGiftOpened) {
        return <span>Received &amp; Opened</span>
    } else {
        switch (item) {
            case CREATED:
                return 'Pending'
            case PRE_PROCESSING:
                return 'Pending'
            case PROCESSING:
                return 'Processing'
            case POST_PROCESSING:
                return 'Processing'
            case PROCESSED:
                if (isPlastic) {
                    if (
                        lineItem.tracking ||
                        (isUSPS && orderStatus === PROCESSED)
                    ) {
                        // USPS doesn't provide a tracking number so mark as sent when order is processed
                        return 'Sent'
                    }
                    return 'Printing'
                } else if (redemptionEmailsSent) {
                    return 'Sent'
                } else {
                    return 'Processed'
                }
            default:
                return item
        }
    }
}

export const getStatusMessage = (statusKey: string) => {
    switch (statusKey) {
        case 'Pending Email Verification':
            return "Please click on the 'Verify my email' button in the email you received after placing the order. BuyGiftCards.com needs to verify the safety and authenticity of the transaction to ensure you have access to the email address you used to place the order."
        case 'Pending Artwork Review':
        case 'Pending Fraud Review':
            return "BuyGiftCards.com's system is reviewing your order and card details for safety and authenticity. This includes verifying any custom card art that may be included in your order to ensure it's safe."
        case 'Pending Account Review':
            return 'Your bulk purchase account application is being reviewed by our team. Once approved, your order will be fulfilled and sent.'
        case 'Account Review Rejected':
            return 'Your bulk purchase account application has been denied. For assistance, click Chat Now to speak with our GiftGuide.'
        case 'Pending':
            return 'Your order is being prepared to print and/or email your gift cards. This is the final step before BuyGiftCards.com fully processes your orders.'
        case 'Processing':
            return 'Your order is undergoing payment processing and your gift cards are being created. The gift cards in your order may have different statuses based on their individual creation and printing status.'
        case 'Completed':
            return 'Your order has been printed/emailed to your recipients. Physical gift cards are mailed on the same day if the order is processed before 4 PM ET, or the following business day. eGift cards are emailed immediately after order processing, regardless of the business day.'
        case 'Cancelled':
            return 'Your order could not be processed and was subsequently cancelled. Any payment authorization made for this order has been cancelled and will disappear from your credit card statement within 5 business days.'
        case 'Refunded':
            return 'Your order has been fully refunded. You can see the total amount refunded to the right of or below your order total.'
        default:
            return ''
    }
}

export const getDeliveryStep = ({
    orderStatus,
    lineItemStatus,
}: {
    orderStatus: string
    lineItemStatus: string
}) => {
    if (orderStatus === ORDERING || orderStatus === PRE_PROCESSING) {
        return 1
    }

    if (orderStatus === PROCESSING || orderStatus === POST_PROCESSING) {
        return 2
    }

    if (lineItemStatus === PROCESSED) {
        return 3
    }

    return 1
}

export function buildFeeDescriptions(feeGroups: any) {
    const feeDescriptions = []

    if (feeGroups) {
        for (let group in feeGroups) {
            feeDescriptions.push(
                <p key={`fee-${group}`}>
                    {group}: $
                    {feeGroups[group]
                        .reduce(
                            (sum: number, fee: { amount: string }) =>
                                sum + Number(fee.amount),
                            0
                        )
                        .toFixed(2)}
                </p>
            )
        }
    }

    return feeDescriptions
}

export function determineKycApplicationStatus(
    kyc:
        | {
              created_at: Date
              approved_at: Date | null
              rejected_at: Date | null
          }[]
        | undefined
) {
    if (!Array.isArray(kyc) || kyc.length < 1) {
        return null
    }

    const latestKycApplication = kyc?.reduce(function (r, a) {
        return r.created_at > a.created_at ? r : a
    })

    if (latestKycApplication?.approved_at) {
        return null
    }

    if (latestKycApplication?.rejected_at) {
        return 'rejected'
    }

    return 'pending'
}
